import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useA11yPreferences } from '@/a11y/A11yPreferencesProvider';
import type { ComuneNodo } from '@/api/types';
import {
  PROVINCE_NAMES,
  PROVINCE_SIGLE,
  comuniPerProvincia,
} from '@/features/mappa/comuni';
import { useComuni } from '@/features/mappa/useComuni';
import type { ElencoNodiComuni } from '@/api/endpoints';
import type { AsyncState } from '@/lib/hooks';
import { messaggioErrore } from '@/lib/erroreUtente';
import { LanguageSwitcher } from '../LanguageSwitcher/LanguageSwitcher';
import { RETE_CIVICA_SECTIONS, type ReteAccent } from './navigation';
import { useMenuConfigurato } from './useMenuConfigurato';
import styles from './Header.module.css';

/**
 * Header istituzionale — allineato alla home del riferimento
 * (https://www.infocomuni.it/puglia/, visual-diff 29/07/2026).
 *
 * UNA sola riga (65px, fondo blu AGID, ombra scura):
 *   marchio → nav a 2 voci con dropdown («Comuni», «Rete Civica») →
 *   utilities: Chat · divisore · lingua · account · accessibilità · ☰.
 *
 * L'elenco dei 257 comuni vive nel dropdown «Comuni» (decisione del
 * committente, 29/07/2026): province con conteggio a sinistra, comuni della
 * provincia scelta a destra, più il link all'indice completo. È
 * l'alternativa testuale alla mappa decorativa dell'hero (WCAG 1.1.1).
 *
 * Le altre destinazioni stanno nel megamenu ☰ («Tutte le sezioni»).
 *
 * Accessibilità:
 *  · ogni dropdown è un disclosure: `aria-expanded` + `aria-controls`,
 *    ESC chiude e riporta il focus al bottone (WCAG 2.4.3), click fuori
 *    chiude, la navigazione chiude.
 *  · un solo dropdown aperto alla volta.
 *  · `NavLink`/`aria-current` sulle voci del megamenu.
 *
 * NOTA: il toggle del tema NON sta più qui (il riferimento non lo ha in
 * barra): è nel pannello accessibilità (A11yPanel), dove resta raggiungibile.
 *
 * NOTA i18n: chiavi nuove con fallback italiano, elencate in
 * `../docs/i18n-migrazione.md` (`nav_chat`, `acct_*`, `nav_all_sections`…).
 */

type DropdownId = 'comuni' | 'rete' | 'account' | 'allmenu';

/** Riferimento stabile: un `[]` nuovo a ogni render rifarebbe i raggruppamenti. */
const ELENCO_VUOTO: readonly ComuneNodo[] = [];

export function Header() {
  const { t } = useTranslation();
  const { setPanelOpen } = useA11yPreferences();
  const location = useLocation();

  const [openDropdown, setOpenDropdown] = useState<DropdownId | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const triggerRefs = useRef<Partial<Record<DropdownId, HTMLButtonElement | null>>>({});

  // I dropdown si chiudono quando si naviga: lasciarli aperti sopra la
  // pagina nuova disorienta e trattiene il focus.
  useEffect(() => {
    setOpenDropdown(null);
  }, [location.pathname]);

  // ESC chiude il dropdown aperto e riporta il focus al suo bottone.
  useEffect(() => {
    if (!openDropdown) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      triggerRefs.current[openDropdown]?.focus();
      setOpenDropdown(null);
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) setOpenDropdown(null);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [openDropdown]);

  const toggleDropdown = useCallback((id: DropdownId) => {
    setOpenDropdown((current) => (current === id ? null : id));
  }, []);

  return (
    <header ref={headerRef} className={styles.header}>
      <div className={`ic-container ${styles.inner}`}>
        <Link to="/" className={styles.brand}>
          {/* Pittogramma (Italia con la Puglia evidenziata) + wordmark a
              immagine, come il riferimento. Un solo link, un solo nome. */}
          <img
            src="/img/italia-puglia.svg"
            alt=""
            aria-hidden="true"
            className={styles.brandSil}
            width={23}
            height={27}
          />
          <img
            src="/img/logo-white.svg"
            alt="InfoComuni"
            className={styles.brandLogo}
            width={243}
            height={38}
          />
        </Link>

        <nav className={styles.nav} aria-label={t('nav_main_label', 'Navigazione principale')}>
          <ComuniDropdown
            open={openDropdown === 'comuni'}
            onToggle={() => toggleDropdown('comuni')}
            triggerRef={(node) => {
              triggerRefs.current.comuni = node;
            }}
          />
          <ReteCivicaDropdown
            open={openDropdown === 'rete'}
            onToggle={() => toggleDropdown('rete')}
            triggerRef={(node) => {
              triggerRefs.current.rete = node;
            }}
          />
        </nav>

        <div className={styles.utils}>
          {/* Chat: nel riferimento punta al widget di assistenza; qui alla
              rotta dell'assistente (il widget va ricostruito accessibile). */}
          {/* `aria-label` uguale al testo visibile: su schermo stretto
              l'etichetta si nasconde e resta il nome accessibile. */}
          <Link
            to="/assistenza/assistente/"
            className={styles.chatLink}
            aria-label={t('nav_chat', 'Chat')}
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              aria-hidden="true"
              className={styles.chatIcon}
            >
              <path
                d="M4 5h16v11H8l-4 4V5Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
            </svg>
            <span className={styles.chatLabel}>{t('nav_chat', 'Chat')}</span>
          </Link>

          <span className={styles.utilsDivider} aria-hidden="true" />

          <LanguageSwitcher />

          <AccountDropdown
            open={openDropdown === 'account'}
            onToggle={() => toggleDropdown('account')}
            triggerRef={(node) => {
              triggerRefs.current.account = node;
            }}
          />

          <button
            type="button"
            className={styles.utilButton}
            onClick={() => setPanelOpen(true)}
            aria-label={t('a11y_panel_open', 'Apri pannello accessibilità')}
            title={`${t('a11y_panel_open', 'Apri pannello accessibilità')} (Alt+A)`}
          >
            <span aria-hidden="true">♿</span>
          </button>

          <AllMenuDropdown
            open={openDropdown === 'allmenu'}
            onToggle={() => toggleDropdown('allmenu')}
            triggerRef={(node) => {
              triggerRefs.current.allmenu = node;
            }}
          />
        </div>
      </div>
    </header>
  );
}

interface DropdownProps {
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly triggerRef: (node: HTMLButtonElement | null) => void;
}

/** Caret ▾ dei bottoni di menu: decorativo.
 *  Geometria del riferimento (visual-diff Rete Civica, 29/07/2026): box
 *  11×11, chevron 24×24 con tratto 2.6 arrotondato. Resta un <svg> inline
 *  (tematizzabile via currentColor) invece del background-image originale;
 *  la rotazione a pannello aperto è in Header.module.css. */
function Caret() {
  return (
    <svg viewBox="0 0 24 24" width="11" height="11" aria-hidden="true">
      <path
        d="M6 9l6 6 6-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Dropdown «Comuni»: province con conteggio + comuni della provincia scelta.
 * È QUI che vivono i comuni (non più in una sezione della home).
 *
 * I dati arrivano dall'API (`useComuni`, richiesta condivisa con picker e
 * mappa), non più da un elenco compilato nel bundle: caricamento, errore ed
 * elenco vuoto sono TRE stati distinti e ognuno si dichiara. L'elenco vuoto
 * non è un caso di scuola — è quello della base dati appena schierata.
 */
function ComuniDropdown({ open, onToggle, triggerRef }: DropdownProps) {
  const { t } = useTranslation();
  const panelId = useId();
  const [sigla, setSigla] = useState<string>('BA');

  const stato = useComuni();
  const elenco = stato.status === 'success' ? stato.data.nodi : ELENCO_VUOTO;

  const gruppi = useMemo(() => comuniPerProvincia(elenco), [elenco]);
  const comuni = gruppi.get(sigla) ?? [];
  // Il conteggio si mostra solo quando è un conteggio: durante il
  // caricamento uno «0» accanto a «Bari» sarebbe un'informazione falsa.
  const contaVisibile = stato.status === 'success';

  return (
    <div className={styles.navItem}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.navButton}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        {t('nav_comuni', 'Comuni')}
        <Caret />
      </button>
      {open ? (
        <div id={panelId} className={`${styles.panel} ${styles.panelComuni}`}>
          <div className={styles.comuniProvince}>
            <p className={styles.panelKicker}>{t('prov_label', 'Provincia')}</p>
            <ul className={styles.provinceList} role="list">
              {PROVINCE_SIGLE.map((s) => (
                <li key={s}>
                  <button
                    type="button"
                    className={styles.provinciaButton}
                    aria-pressed={s === sigla}
                    onClick={() => setSigla(s)}
                  >
                    <span className={styles.provinciaSigla}>{s}</span>
                    <span className={styles.provinciaNome}>{PROVINCE_NAMES[s] ?? s}</span>
                    {contaVisibile ? (
                      <span className={styles.provinciaCount}>
                        {(gruppi.get(s) ?? []).length}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
            <Link to="/comuni/" className={styles.panelCta}>
              {t('comuni_title', 'Tutti i Comuni')} <span aria-hidden="true">→</span>
            </Link>
          </div>
          <div className={styles.comuniElenco}>
            <p className={styles.panelKicker}>
              {PROVINCE_NAMES[sigla] ?? sigla}
              {contaVisibile ? ` (${comuni.length})` : null}
            </p>
            <StatoComuni stato={stato} vuoto={comuni.length === 0} />
            {stato.status === 'success' && comuni.length > 0 ? (
              <ul className={styles.comuniList} role="list">
                {comuni.map((comune) => (
                  <li key={comune.slug}>
                    <Link to={`/comuni/${comune.slug}/`} className={styles.comuneLink}>
                      {comune.nome}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Lo stato dell'elenco comuni, DETTO. Caricamento e guasto sono cose
 * diverse fra loro e diverse ancora da «non ci sono comuni»: appiattirli in
 * un pannello vuoto lascerebbe credere che la Puglia non abbia comuni.
 */
function StatoComuni({
  stato,
  vuoto,
}: {
  readonly stato: AsyncState<ElencoNodiComuni>;
  readonly vuoto: boolean;
}) {
  const { t } = useTranslation();

  if (stato.status === 'loading') {
    return (
      <p className={styles.panelStato} role="status">
        {t('loading', 'Caricamento…')}
      </p>
    );
  }
  if (stato.status === 'error') {
    const messaggio = messaggioErrore(stato.error, t);
    return (
      <p className={styles.panelStato} role="alert">
        {messaggio.titolo} — {messaggio.testo}
      </p>
    );
  }
  if (vuoto) {
    return (
      <p className={styles.panelStato}>
        {t('comuni_none', 'Nessun comune pubblicato al momento.')}
      </p>
    );
  }
  return null;
}

/** Classe della piastrella icona per accento (visual-diff 29/07/2026):
 *  mappa esplicita, non accesso indicizzato su `styles` (TS strict). */
const RETE_ACCENT_CLASS: Record<ReteAccent, string> = {
  lavoro: styles.reteIconLavoro ?? '',
  startup: styles.reteIconStartup ?? '',
  ets: styles.reteIconEts ?? '',
  volontariato: styles.reteIconVolontariato ?? '',
};

/**
 * Dropdown «Rete Civica»: le 4 sezioni del riferimento, ognuna come card
 * icona + (titolo + badge) + descrizione (visual-diff 29/07/2026).
 * La CTA «Vai alla Rete Civica» NON c'è: sul riferimento è nascosta e la
 * destinazione /rete-civica/ resta raggiungibile dal footer (Istituzionale).
 * Il pannello resta un disclosure (aria-expanded + aria-controls): il
 * role=menu del riferimento è ARIA scorretto per un elenco di link.
 */
function ReteCivicaDropdown({ open, onToggle, triggerRef }: DropdownProps) {
  const { t } = useTranslation();
  const panelId = useId();

  return (
    <div className={styles.navItem}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.navButton}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        {t('nav_rete', 'Rete Civica')}
        <Caret />
      </button>
      {open ? (
        <div id={panelId} className={`${styles.panel} ${styles.panelRete}`}>
          {/* Chiave dedicata, NON `nav_rete`: il kicker del riferimento ha
              il sottotitolo «· lavoro e comunità» che il nome della voce di
              menu (trigger, footer) non deve ereditare. */}
          <p className={styles.panelKicker}>
            {t('rete_kicker', 'Rete Civica · lavoro e comunità')}
          </p>
          <ul className={styles.reteList} role="list">
            {RETE_CIVICA_SECTIONS.map((section) => (
              <li key={section.to}>
                <Link to={section.to} className={styles.reteCard}>
                  <span
                    className={`${styles.reteIcon} ${RETE_ACCENT_CLASS[section.accent]}`}
                    aria-hidden="true"
                  >
                    {/* Icona del riferimento: 21×21, tratto bianco 2. */}
                    <svg viewBox="0 0 24 24" width="21" height="21">
                      <path
                        d={section.iconPath}
                        fill="none"
                        stroke="#ffffff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span className={styles.reteBody}>
                    <span className={styles.reteTitle}>
                      {t(section.labelKey, section.fallback)}
                      <span className={styles.reteTag}>
                        {t(section.tagKey, section.tagFallback)}
                      </span>
                    </span>
                    <span className={styles.reteDesc}>
                      {t(section.descKey, section.descFallback)}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/** Account: stato ospite dichiarato + accesso/registrazione (back office). */
function AccountDropdown({ open, onToggle, triggerRef }: DropdownProps) {
  const { t } = useTranslation();
  const panelId = useId();

  return (
    <div className={styles.utilItem}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.utilButton}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={t('acct_label', 'Account')}
        title={t('acct_label', 'Account')}
        onClick={onToggle}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <circle cx="12" cy="8" r="3.6" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path
            d="M4.5 20a7.5 7.5 0 0 1 15 0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {open ? (
        <div id={panelId} className={`${styles.panel} ${styles.panelAccount}`}>
          {/* Lo stato si DICHIARA: nessuna sessione pubblica, quindi ospite. */}
          <p className={styles.accountState}>
            {t('acct_guest', 'Ospite - non registrato')}
          </p>
          <ul className={styles.accountList} role="list">
            <li>
              <Link to="/redazione/" className={styles.panelLink}>
                {t('acct_login', 'Accedi')}
              </Link>
            </li>
            <li>
              <Link to="/redazione/" className={styles.panelLink}>
                {t('acct_register', 'Registrati')}
              </Link>
            </li>
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Megamenu ☰ «Tutte le sezioni»: le destinazioni fuori dai due dropdown.
 *
 * Le voci arrivano dalla configurazione della redazione quando c'è
 * (decisione 82), da `MAIN_NAV` quando non c'è: `useMenuConfigurato` decide,
 * e la resa è la stessa nei due casi.
 */
function AllMenuDropdown({ open, onToggle, triggerRef }: DropdownProps) {
  const { t } = useTranslation();
  const panelId = useId();
  const { voci, configurato } = useMenuConfigurato();

  return (
    <div className={styles.utilItem}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.utilButton} ${styles.allmenuButton}`}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={t('nav_all_sections', 'Tutte le sezioni')}
        title={t('nav_all_sections', 'Tutte le sezioni')}
        onClick={onToggle}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path
            d="M3 6h18M3 12h18M3 18h18"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {open ? (
        <nav
          id={panelId}
          className={`${styles.panel} ${styles.panelAllmenu}`}
          aria-label={t('nav_all_sections', 'Tutte le sezioni')}
        >
          <ul className={styles.allmenuList} role="list">
            {voci.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} className={styles.panelLink}>
                  {/* Le etichette configurate arrivano già nella lingua
                      richiesta: cercarle nel dizionario darebbe la chiave
                      grezza. Quelle predefinite passano da `t()` come prima. */}
                  {configurato ? item.fallback : t(item.labelKey, item.fallback)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
    </div>
  );
}
